import { createIsomorphicFn } from '@tanstack/react-start';
import { Effect } from 'effect';
import { authClient } from '@/lib/auth-client';
import { clientRuntime } from '@/runtimes/client-runtime';
import { getServerSessionImpl } from './get-session.server';
import {
  orUnavailable,
  remoteSessionCheck,
  toSessionCheck,
} from './get-session-shared';

export type SessionInfo = {
  userId: string;
  email: string;
  role: string | null;
};

/**
 * Result of a session check. `Unavailable` means the check itself failed —
 * the auth API errored or was unreachable — and says nothing about whether
 * the user is signed in. Route guards must not treat it as signed-out: a
 * guard that ejects on `Unavailable` turns any infra blip into a forced
 * logout. Guards on pages that render fine for anonymous users fail open
 * (render, let the client re-check); only privileged UI (admin) fails
 * closed.
 */
export type SessionCheck =
  | { readonly _tag: 'Authenticated'; readonly session: SessionInfo }
  | { readonly _tag: 'Unauthenticated' }
  | { readonly _tag: 'Unavailable' };

const clientSessionCheck = (): Effect.Effect<SessionCheck> =>
  orUnavailable(
    remoteSessionCheck(() => authClient.getSession()).pipe(
      Effect.map(toSessionCheck),
    ),
  ).pipe(Effect.annotateLogs({ module: 'GetSession' }));

/**
 * Isomorphic session check using the better-auth client SDK.
 * - SSR: verifies the signed better-auth cookie cache locally (no network),
 *   falling back to the auth API only when the cache is absent or expired
 * - Client: uses the default authClient (same-origin, browser sends cookies)
 *
 * No createServerFn needed — createIsomorphicFn handles the split at compile time.
 */
export const getSession: () => Promise<SessionCheck> = createIsomorphicFn()
  .server(() => getServerSessionImpl())
  .client(() => clientRuntime.runPromise(clientSessionCheck()));
